import React from 'react'
import { useParams } from 'react-router-dom'

import Dropdown from 'components/Dropdown'
import VisuallyHidden from 'components/VisuallyHidden'
import { ChangeRoleConfirmationModal } from './ChangeRoleConfirmationModal'

import { useToast } from 'containers/ToastManager'
import { useModalContext } from 'containers/ModalManager'
import { ToastContextValues } from 'containers/ToastManager/ToastContext'

import {
  TableButton,
  EllipsisIcon,
  DropdownHeader,
  DropdownItemDescription,
} from './MemberActions.styles'

import {
  WorkspaceMembershipFragment,
  BillingSeatsInfoFragment,
  BillingPlanFragment,
  useUpdateWorkspaceMemberMutation,
  PaymentDetailsFragment,
  useResendWorkspaceInviteMutation,
  GetWorkspaceMembershipsDocument,
} from 'gqlTypes'

import { capitalize } from 'utils'

// we are importing more specifically here, because modals are importing components from the
// root components index.ts file
import { EditorDowngradeModal } from '../../modals/EditorDowngradeModal/EditorDowngradeModal'
import { ViewerUpgradeModal } from '../../modals/ViewerUpgradeModal/ViewerUpgradeModal'
import { RemoveWorkspaceMemberModal } from '../../modals/RemoveWorkspaceMemberModal/RemoveWorkspaceMemberModal'

import { getMemberName } from '../../utils/members'
import {
  isWorkspaceSubscriptionCanceled,
  isWorkspaceSubscriptionTrialling,
} from '../../utils/status'

// Usage of BillingStatus enum is needed to make the expected strings match the BE ones
// eslint-disable-next-line no-restricted-imports
import { BillingStatus } from 'gqlTypes/expansiveTypes'
import { ErrorHandler } from 'modules/tracing'
import { RouteParams } from 'constants/routes'
import { useFlag } from 'constants/flags'

type ShowToast = ToastContextValues['showToast']

interface ChangeMemberRoleDropdownItemProps {
  membershipId: string
  memberCurrentRole: WorkspaceMembershipFragment['role']
  showToast: ShowToast
}

export const ChangeMemberRoleDropdownItem: React.FC<ChangeMemberRoleDropdownItemProps> = ({
  membershipId,
  memberCurrentRole,
  showToast,
}) => {
  // TODO: Remove usages of finance-role feature flag
  // https://github.com/sketch-hq/Cloud/issues/9959
  const isFinanceRoleEnabled = useFlag('finance-role')

  const { workspaceId } = useParams<RouteParams<'WORKSPACE_SETTINGS'>>()
  const { showModal, hideModal } = useModalContext()

  const isAdmin = memberCurrentRole === 'ADMIN'
  const isFinance = memberCurrentRole === 'FINANCE'

  const [
    updateMember,
    { loading: updateMemberLoading },
  ] = useUpdateWorkspaceMemberMutation({
    onError: ({ message }) => {
      showToast(message, 'negative')
      hideModal()
    },
    onCompleted: data => {
      hideModal()
      const { membership } = data?.updateWorkspaceMember || {}

      // Apollo might return empty object as data if Cache Miss would happen
      if (!membership) {
        ErrorHandler.shouldNeverHappen(
          `updateWorkspaceMember should return valid data`
        )
        return
      }

      const { role } = membership!
      showToast(
        `${getMemberName(membership)} access changed to ${capitalize(role!)}`
      )
    },
    refetchQueries: [
      {
        query: GetWorkspaceMembershipsDocument,
        variables: {
          workspaceId,
        },
      },
    ],
  })

  return (
    <>
      <Dropdown.Item
        onClick={() => {
          showModal(ChangeRoleConfirmationModal, {
            variant: 'ADMIN',
            membershipId,
            role: memberCurrentRole,
            updateMember,
            loading: updateMemberLoading,
          })
        }}>
        {isAdmin ? 'Remove as Admin' : 'Make Admin'}
        <DropdownItemDescription>
          {isAdmin
            ? 'Can no longer manage the Workspace, add or remove members, and edit roles'
            : 'Can manage the Workspace, add or remove members, and edit roles'}
        </DropdownItemDescription>
      </Dropdown.Item>

      {isFinanceRoleEnabled ? (
        <Dropdown.Item
          onClick={() => {
            showModal(ChangeRoleConfirmationModal, {
              variant: 'FINANCE',
              membershipId,
              role: memberCurrentRole,
              updateMember,
              loading: updateMemberLoading,
            })
          }}>
          {isFinance ? 'Remove Finance' : 'Make Finance'}
          <DropdownItemDescription>
            {isFinance
              ? 'Can no longer manage billing and Subscription on behalf of Admin'
              : 'Can manage billing and Subscription on behalf of Admin'}
          </DropdownItemDescription>
        </Dropdown.Item>
      ) : null}
    </>
  )
}

interface ResendMemberInviteProps {
  membershipId: string
  userEmail: string
  showToast: ShowToast
}

const ResendMemberInvite: React.FC<ResendMemberInviteProps> = props => {
  const { membershipId, userEmail, showToast } = props

  const [resendInvite, { loading }] = useResendWorkspaceInviteMutation({
    variables: { membershipId },
    onCompleted: () => showToast(`Invite to ${userEmail} sent!`),
    onError: 'show-toast',
  })

  return (
    <Dropdown.Item disabled={loading} onClick={() => resendInvite()}>
      Resend Invitation
    </Dropdown.Item>
  )
}

interface MemberActionsProps {
  member: WorkspaceMembershipFragment
  billingStatus: BillingStatus
  customerId: string
  seats: BillingSeatsInfoFragment
  plan: BillingPlanFragment
  paymentDetails?: PaymentDetailsFragment
  workspaceId: string
  isSsoEnabled: boolean
}

export const MemberActions: React.FC<MemberActionsProps> = props => {
  const {
    billingStatus,
    member,
    customerId,
    seats,
    plan,
    paymentDetails,
    workspaceId,
    isSsoEnabled,
  } = props

  const { showToast } = useToast()
  const { showModal } = useModalContext()

  const email = member.user?.email || member.invite?.email
  const invitePending = !member.acceptedAt

  const handleEditorButtonClick = () => {
    if (isWorkspaceSubscriptionCanceled(billingStatus)) {
      showToast(
        'You need an active subscription to edit workspace members',
        'negative'
      )
    } else {
      showModal(ViewerUpgradeModal, {
        member,
        isOnTrial: isWorkspaceSubscriptionTrialling(billingStatus),
        customerId,
        seats,
        plan,
        paymentDetails: paymentDetails!,
      })
    }
  }

  const handleViewerButtonClick = () => {
    if (isWorkspaceSubscriptionCanceled(billingStatus)) {
      showToast(
        'You need an active subscription to edit workspace members',
        'negative'
      )
    } else {
      showModal(EditorDowngradeModal, { member })
    }
  }

  const handleToggleEditorClick = () => {
    if (member.isEditor) {
      handleViewerButtonClick()
    } else {
      handleEditorButtonClick()
    }
  }

  let dropdownContent = null

  if (member.role !== 'GUEST') {
    dropdownContent = (
      <>
        <DropdownHeader>Access</DropdownHeader>
        <Dropdown.Item onClick={handleToggleEditorClick}>
          {member.isEditor ? 'Change to Viewer...' : 'Change to Editor...'}
          <DropdownItemDescription>
            {member.isEditor
              ? 'Browse, inspect and comment on documents in the web app only'
              : 'Browse, inspect and comment in the web app — and create and edit documents in the Mac app'}
          </DropdownItemDescription>
        </Dropdown.Item>
        {!member.isOwner && (
          <>
            <Dropdown.Divider />
            <DropdownHeader>Role</DropdownHeader>
            <ChangeMemberRoleDropdownItem
              membershipId={member.identifier}
              memberCurrentRole={member.role!}
              showToast={showToast}
            />
            <Dropdown.Divider />
            {invitePending && (
              <ResendMemberInvite
                membershipId={member.identifier}
                userEmail={email!}
                showToast={showToast}
              />
            )}
            <Dropdown.Item
              intent="negative"
              onClick={() => {
                showModal(RemoveWorkspaceMemberModal, { member, workspaceId })
              }}>
              Remove from Workspace&hellip;
            </Dropdown.Item>
          </>
        )}
      </>
    )
  } else {
    dropdownContent = (
      <>
        {!isSsoEnabled && (
          <>
            <DropdownHeader>Access</DropdownHeader>
            <Dropdown.Item onClick={handleViewerButtonClick}>
              Change to Member (Viewer)…
              <DropdownItemDescription>
                Browse, inspect and comment on documents in the web app only
              </DropdownItemDescription>
            </Dropdown.Item>
            <Dropdown.Item onClick={handleEditorButtonClick}>
              Change to Member (Editor)…
              <DropdownItemDescription>
                All of the above, and create and edit documents in the Mac app
              </DropdownItemDescription>
            </Dropdown.Item>
            <Dropdown.Divider />
          </>
        )}
        <Dropdown.Item
          intent="negative"
          onClick={() => {
            showModal(RemoveWorkspaceMemberModal, { member, workspaceId })
          }}>
          Remove from All Documents <br />& Workspace&hellip;
        </Dropdown.Item>
      </>
    )
  }

  return (
    <Dropdown
      contentPadding="12px 0"
      placement="bottom-end"
      maxWidth="240px"
      /* Force dropdown container to not fill the parent div */
      style={{ display: 'inline-block' }}
      toggle={
        <TableButton active data-testid="edit-member-access">
          <VisuallyHidden>Edit</VisuallyHidden>
          <EllipsisIcon />
        </TableButton>
      }>
      {dropdownContent}
    </Dropdown>
  )
}
